import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Form, Button, OverlayTrigger, Popover, Modal } from "react-bootstrap";
import { useNavigate, useParams, useBlocker } from "react-router-dom";
import axios from "axios";
import { Formik, FieldArray } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import Card from "../../../components/Card";
import url from "../../../configs/config";
import FocusError from "../../../components/error/error-focus";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import InputField from "../../../components/input/input-field";
import { roleTypes } from "../../../data/roleType";
import FadeLoaderComponent from "../../../components/loader-component";
import * as SettingSelector from "../../../store/setting/selectors";
const FormObserver = ({ dirty, setHasUnsavedChanges }) => {
  useEffect(() => {
    setHasUnsavedChanges(dirty);
  }, [dirty, setHasUnsavedChanges]);
  return null;
};

const SystemEdit = () => {
  const alertRef = useRef(null);
  const navigate = useNavigate();
  const { id } = useParams();

  const [errors, setErrors] = useState({});
  const [decodedToken, setDecodedToken] = useState();
  const [loading, setLoading] = useState(true);
  const [systemData, setSystemData] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showPopover, setShowPopover] = useState(null);

  const token = localStorage.getItem("coyote-token");
  const permissions = useSelector(SettingSelector.set_permissions);
  const hasUpdatePermission = permissions?.system?.update === true;
  const hasDeletePermission = permissions?.system?.delete === true;
  const hasPhaseDeletePermission = permissions?.phase?.delete === true;

  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const allowNavigationRef = useRef(false);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (allowNavigationRef.current) return false;
    return (
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
    );
  });

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (blocker.state === "blocked") {
      if (hasUnsavedChanges && !allowNavigationRef.current) {
        setShowDiscardModal(true);
        setPendingNavigation(() => blocker.proceed);
      } else {
        blocker.reset();
        setShowDiscardModal(false);
        setPendingNavigation(null);
      }
    }
  }, [blocker.state, hasUnsavedChanges, blocker]);

  useEffect(() => {
    return () => {
      if (blocker.state === "blocked" && !allowNavigationRef.current)
        blocker.reset();
      allowNavigationRef.current = false;
    };
  }, [blocker]);

  const handleDiscardChanges = () => {
    if (blocker.state === "blocked") {
      allowNavigationRef.current = true;
      setShowDiscardModal(false);
      setPendingNavigation(null);
      if (pendingNavigation) {
        pendingNavigation();
      } else if (blocker.proceed) {
        blocker.proceed();
      }
    }
  };

  const handleCancelDiscard = () => {
    allowNavigationRef.current = false;
    if (blocker.state === "blocked") blocker.reset();
    setShowDiscardModal(false);
    setPendingNavigation(null);

    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  useEffect(() => {
    const fetchSystemData = async () => {
      if (!id || !token) return;
      setLoading(true);
      try {
        const config = { headers: { Authorization: `${token}` } };
        
        const allSystemsRes = await axios.get(`${url.backendURL}system/all`, config);
        const currentSystem = allSystemsRes.data?.response?.data?.find(s => s.id === parseInt(id));

        const phasesRes = await axios.get(`${url.backendURL}system/${id}/phases`, config);
        const phasesData = phasesRes.data?.response?.data || [];
        
        const phasesFormatted = phasesData.length > 0 
          ? phasesData.map(p => ({ id: p.id, name: p.name || "", code: p.code || "" }))
          : [{ name: "", code: "" }];

        setSystemData({
          name: currentSystem?.name || "",
          code: currentSystem?.code || "",
          phases: phasesFormatted,
        });

      } catch (err) {
        handleError(err, "system-fetch");
      } finally {
        setLoading(false);
      }
    };

    fetchSystemData();
  }, [id, token]);

  const togglePopover = (popoverId) => {
    setShowPopover(showPopover === popoverId ? null : popoverId);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (showPopover !== null) {
        setShowPopover(null);
      }
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [showPopover]);

  const handleDeletePhase = async (phaseId, phaseName, index, remove) => {
    setDeleteLoading(true);
    try {
      const config = { headers: { Authorization: `${token}` } };
      await axios.delete(`${url.backendURL}phase/${phaseId}`, config);
      
      remove(index);
      setSystemData(prevData => ({
        ...prevData,
        phases: prevData.phases.filter((_, i) => i !== index),
      }));
      setAlert({
        message: `Phase "${phaseName}" deleted successfully`,
        type: "success",
        show: true,
        alertFor: "system-edit",
      });
      if (alertRef.current) {
        alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      setShowPopover(null);
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 3000);
    } catch (err) {
      handleError(err, "system-edit");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    try {
      const config = {
        headers: { Authorization: `${token}` },
      };
      const response = await axios.put(
        `${url.backendURL}system/${id}`,
        values,
        config
      );

      if (alertRef.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      setHasUnsavedChanges(false);
      allowNavigationRef.current = true;
      if (blocker.state === "blocked") blocker.reset();

      setAlert({
        message: response.data.message || "System updated successfully",
        type: "success",
        show: true,
        alertFor: "system-edit",
      });

      setErrors({});
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
        navigate(`/dashboard/app/system-list`);
      }, 2000);
    } catch (err) {
      handleError(err, "system-edit");
    } finally {
      setSubmitting(false);
    }
  };

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (
      response?.data?.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        }
      );
    } else {
      const responseErrors = response?.data?.error || {};
      setErrors(responseErrors);
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .matches(
        /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
        "System name must contain at least one letter or number"
      )
      .min(1, "System name must be at least 1 character long")
      .max(255, "System name must not exceed 255 characters")
      .trim()
      .required("System Name is required"),
    code: Yup.string()
      .min(1, "Code must be at least 1 character long")
      .max(20, "Code must not exceed 20 characters")
      .trim()
      .required("Code is required"),
    phases: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().when(["code"], {
            is: (code) => code && code.trim(),
            then: (schema) =>
              schema
                .matches(
                  /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
                  "Phase name must contain at least one letter or number"
                )
                .min(1, "Phase name must be at least 1 character long")
                .max(255, "Phase name must not exceed 255 characters")
                .trim()
                .required("Phase name is required"),
            otherwise: (schema) =>
              schema
                .matches(
                  /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
                  "Phase name must contain at least one letter or number"
                )
                .min(1, "Phase name must be at least 1 character long")
                .max(255, "Phase name must not exceed 255 characters")
                .trim()
                .nullable(),
          }),
          code: Yup.string().when(["name"], {
            is: (name) => name && name.trim(),
            then: (schema) =>
              schema
                .min(1, "Code must be at least 1 character long")
                .max(20, "Code must not exceed 20 characters")
                .trim()
                .required("Code is required"),
            otherwise: (schema) =>
              schema
                .min(1, "Code must be at least 1 character long")
                .max(20, "Code must not exceed 20 characters")
                .trim()
                .nullable(),
          }),
        }, [["name", "code"]])
      )
      .max(10, "Maximum 10 phases allowed")
      .nullable(),
  });

  const handleReset = (resetForm) => {
    resetForm();
    setErrors({});
  };

  if (loading) {
    return (
      <Row>
        <Col xl="12" lg="12">
          <Card>
            <Card.Body className="text-center">
              <FadeLoaderComponent />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  if (!systemData) {
    return (
      <Row>
        <Col xl="12" lg="12">
          <Card>
            <Card.Body className="text-center">
              <div>System not found</div>
              <Button
                variant="btn btn-primary"
                onClick={() => navigate("/dashboard/app/system-list")}
                className="mt-3"
              >
                Back to List
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <>
      <Formik
        initialValues={systemData}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
        validate={(values) => {
          const errors = {};
          if (values.phases && values.phases.length > 1) {
            const phaseNames = [];
            const phaseCodes = [];
            const phaseErrors = [];

            values.phases.forEach((phase, index) => {
              const phaseError = {};
              if (phase.name) {
                const normalizedName = phase.name.trim().toLowerCase();
                if (normalizedName && phaseNames.includes(normalizedName)) {
                  phaseError.name = "Phase with same name already exists";
                } else if (normalizedName) {
                  phaseNames.push(normalizedName);
                }
              }

              if (phase.code) {
                const normalizedCode = phase.code.trim().toLowerCase();
                if (normalizedCode && phaseCodes.includes(normalizedCode)) {
                  phaseError.code = "Phase with same code already exists";
                } else if (normalizedCode) {
                  phaseCodes.push(normalizedCode);
                }
              }

              if (Object.keys(phaseError).length > 0) {
                phaseErrors[index] = phaseError;
              }
            });

            if (phaseErrors.length > 0) {
              errors.phases = phaseErrors;
            }
          }
          return errors;
        }}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          resetForm,
          errors: formikErrors,
          touched,
          dirty,
        }) => (
          <Form onSubmit={handleSubmit}>
            <FormObserver dirty={dirty} setHasUnsavedChanges={setHasUnsavedChanges} />
            <FocusError />
            <Row>
              <Col md="12" ref={alertRef}>
                <Card>
                  <Card.Header className="d-flex justify-content-between">
                    <div
                      className="breadcrumb header-title"
                      style={{ cursor: "pointer" }}
                    >
                      <h4
                        onClick={() => navigate("/dashboard/app/system-list")}
                        className="text-primary card-title"
                        style={{ cursor: "pointer" }}
                      >
                        <span>System</span>
                      </h4>
                      <h4 className="mx-2 card-title">{">"}</h4>
                      <h4 className="card-title">Edit</h4>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <AlertNotificationSimple
                      type={alert.type}
                      message={alert.message}
                      show={alert.show}
                      onClose={() => setAlert({ ...alert, show: false })}
                    />
                    <div className="new-system-info">
                      <Row>
                        <InputField
                          className="col-md-6 form-group"
                          type="text"
                          name="name"
                          placeholder="Enter name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          asterick={true}
                          errorMessage={errors?.name}
                          disabled={
                            !hasUpdatePermission &&
                            decodedToken?.roles?.some(
                              (role) => role.role_type !== roleTypes.SUPER_ADMIN
                            )
                          }
                          label="Name"
                        />
                        <InputField
                          className="col-md-6 form-group"
                          type="text"
                          name="code"
                          placeholder="Enter code"
                          value={values.code}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          asterick={true}
                          errorMessage={errors?.code}
                          disabled={
                            !hasUpdatePermission &&
                            decodedToken?.roles?.some(
                              (role) => role.role_type !== roleTypes.SUPER_ADMIN
                            )
                          }
                          label="Code"
                        />
                      </Row>

                      <hr className="my-4" />

                      <div className="phases-info">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5 className="text-primary mb-0">Phase Information (Optional)</h5>
                        </div>

                        <FieldArray name="phases">
                          {({ push, remove }) => (
                            <>
                              {values.phases.map((phase, index) => (
                                <div
                                  key={index}
                                  className="phase-item mb-4 p-3"
                                  style={{ border: "1px solid #e9ecef", borderRadius: "8px" }}
                                >
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0">Phase {index + 1}</h6>
                                    {phase.id && (hasPhaseDeletePermission || decodedToken?.roles?.some((role) => role.role_type === roleTypes.SUPER_ADMIN)) ? (
                                      <OverlayTrigger
                                        trigger="click"
                                        show={showPopover === `remove-phase-${index}`}
                                        onToggle={() => togglePopover(`remove-phase-${index}`)}
                                        placement="top"
                                        overlay={
                                          <Popover id={`popover-remove-phase-${index}`}>
                                            <Popover.Header as="h3">
                                              <strong>Delete Phase</strong> <span>({phase.name || "Unnamed"})</span>?
                                            </Popover.Header>
                                            <Popover.Body>
                                              Once deleted, this action cannot be undone.
                                              <div className="d-flex justify-content-end mt-2">
                                                <Button
                                                  size="sm"
                                                  variant="danger"
                                                  className="me-2"
                                                  disabled={deleteLoading}
                                                  onClick={async (e) => {
                                                    e.preventDefault();
                                                    if (!deleteLoading) {
                                                      await handleDeletePhase(phase.id, phase.name || "Unnamed", index, remove);
                                                    }
                                                  }}
                                                >
                                                  {deleteLoading ? "Deleting..." : "Delete"}
                                                </Button>
                                                <Button
                                                  size="sm"
                                                  variant="outline-secondary"
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    togglePopover(`remove-phase-${index}`);
                                                  }}
                                                >
                                                  Cancel
                                                </Button>
                                              </div>
                                            </Popover.Body>
                                          </Popover>
                                        }
                                      >
                                        <Button
                                          type="button"
                                          variant="btn btn-outline-danger btn-sm"
                                          onClick={(e) => e.preventDefault()}
                                        >
                                          Remove Phase
                                        </Button>
                                      </OverlayTrigger>
                                    ) : (
                                      !phase.id && values.phases.length > 1 && (hasUpdatePermission || decodedToken?.roles?.some((role) => role.role_type === roleTypes.SUPER_ADMIN)) && (
                                        <Button
                                          type="button"
                                          variant="btn btn-outline-danger btn-sm"
                                          onClick={() => remove(index)}
                                        >
                                          Remove Phase
                                        </Button>
                                      )
                                    )}
                                  </div>

                                  <Row>
                                    <InputField
                                      className="col-md-6 form-group"
                                      type="text"
                                      name={`phases[${index}].name`}
                                      placeholder="Enter phase name"
                                      value={phase.name}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      asterick={false}
                                      errorMessage={
                                        errors?.phases?.[index]?.name
                                      }
                                      disabled={
                                        !hasUpdatePermission &&
                                        decodedToken?.roles?.some(
                                          (role) => role.role_type !== roleTypes.SUPER_ADMIN
                                        )
                                      }
                                      label="Phase Name"
                                    />
                                    <InputField
                                      className="col-md-6 form-group"
                                      type="text"
                                      name={`phases[${index}].code`}
                                      placeholder="Enter code"
                                      value={phase.code}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      asterick={false}
                                      errorMessage={
                                        errors?.phases?.[index]?.code
                                      }
                                      disabled={
                                        !hasUpdatePermission &&
                                        decodedToken?.roles?.some(
                                          (role) => role.role_type !== roleTypes.SUPER_ADMIN
                                        )
                                      }
                                      label="Code"
                                    />
                                  </Row>
                                </div>
                              ))}

                              <div className="mb-4">
                                <Button
                                  type="button"
                                  variant="btn btn-primary"
                                  onClick={() => push({ name: "", code: "" })}
                                  disabled={values.phases.length >= 10 || (!hasUpdatePermission && decodedToken?.roles?.some((role) => role.role_type !== roleTypes.SUPER_ADMIN))}
                                >
                                  + Add New Phase
                                </Button>
                              </div>
                            </>
                          )}
                        </FieldArray>
                      </div>
                      <hr />
                      <div className="d-flex flex-wrap flex-md-nowrap justify-content-end gap-1 gap-md-2">
                        <Button
                          type="button"
                          className="me-2 me-md-5 px-3 px-md-4"
                          variant="btn btn-light"
                          onClick={() => handleReset(resetForm)}
                        >
                          Reset
                        </Button>

                        <Button
                          type="submit"
                          disabled={isSubmitting || (!hasUpdatePermission && decodedToken?.roles?.some((role) => role.role_type !== roleTypes.SUPER_ADMIN))}
                          variant="btn btn-primary"
                          className="me-2 me-md-5 px-3 px-md-4"
                        >
                          {isSubmitting ? "Updating..." : "Update"}
                        </Button>

                        <Button
                          type="button"
                          className="px-3 px-md-4"
                          variant="btn btn-danger"
                          onClick={() => navigate("/dashboard/app/system-list")}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>

      <Modal
        show={showDiscardModal}
        onHide={handleCancelDiscard}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header className="custom-modal-header d-flex justify-content-center items-center relative">
          <Modal.Title>Unsaved Changes</Modal.Title>
          <button
            type="button"
            className="btn-close-svg"
            style={{ position: "absolute", right: "1rem" }}
            onClick={handleCancelDiscard}
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </Modal.Header>
        <Modal.Body className="text-center px-4 pt-2">
          <p
            className="text-muted mb-4 px-2"
            style={{ fontSize: "1rem", lineHeight: "1.5" }}
          >
            You have unsaved changes. Do you want to discard these? Click update
            to save the changes.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-end pb-4 pt-0">
          <Button
            type="button"
            className="btn btn-white me-4"
            variant="btn btn-white"
            onClick={handleDiscardChanges}
            style={{
              borderRadius: "25px",
              fontWeight: "500",
              minWidth: "100px",
              borderColor: "#6c757d",
              color: "#6c757d",
            }}
          >
            Discard
          </Button>
          <Button
            className="px-4 py-2"
            style={{
              backgroundColor: "#3a57e8",
              borderColor: "#3a57e8",
              borderRadius: "25px",
              fontWeight: "500",
              minWidth: "100px",
            }}
            onClick={handleCancelDiscard}
          >
            Continue
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SystemEdit;